import React from 'react';
import type {DataConfig, Fetch} from '@sail/data';
import {getV1SchemaConfig} from '@stripe-internal/data-v1';

import {DataProvider, createCache} from '@sail/data';
import {UiProvider} from '@sail/stripelet';
import {ObservabilityProvider} from '@sail/observability';
import {IntlProvider, message, useIntl} from '@sail/intl';
import {ErrorState} from '@stripe-internal/connect-embedded-lib';
import type {IntlConfig} from '@sail/intl';
import type {UiConfig} from '@sail/stripelet';
import type {ReportError} from '@stripe-internal/connect-embedded-lib/dist/context/errorReporting/ErrorTypes';
// eslint-disable-next-line @sail/data-no-imperative-methods
import type {ApolloLink} from '@apollo/client';
import {getV2SchemaConfig} from '@stripe-internal/data-v2';
import {
  getStripeAccountsHost,
  getStripeApiHost,
} from '../data-layer-client/buildStripeClient';
import {getCurrentScriptUrlContext} from '../utils/getCurrentScriptUrlContext';
import type {FetchOverrideFn} from '../data-layer-client/buildFetchOverride';
import type {ConnectElementImportKeys} from './ConnectJSInterface/ConnectElementList';
import {CONNECT_ELEMENT_IMPORTS} from './ConnectJSInterface/ConnectElementList';
import type {ConnectJsOptions} from './ConnectJsOptions';
import initSdkIntl from '../intl/getIntlConfig';
import {getObservabilityConfig} from './getObservabilityConfig';
import type {IFrameMessenger} from '../data-layer-client/FrameMessenger';
import {
  DEFAULT_GLOBALLY_EXPECTED_ERRORS,
  DEFAULT_GLOBALLY_MUTED_ERRORS,
  DEFAULT_HOSTED_ONBOARDING_EXPECTED_ERRORS,
  DEFAULT_READONLY_EXPECTED_ERRORS,
  READONLY_HOST_APPS,
} from '../utils/telemetry/connectElementAnalyticsSender';
import {hostedOnboardingHostApps} from '../utils/getHostApp';
import type {HostApp} from './ConnectJSInterface/InitAndUpdateOptionsTypes';
import {useEmbeddedInvalidOrExpiredSession} from './EmbeddedInvalidOrExpiredSessionContext';
import {createDetectExpiredOrInvalidSessionLink} from '../graphql/links/createDetectExpiredOrInvalidSessionLink';

const defaultErrorMessage = message({
  id: 'stripelet.errorBoundary.error',
  description: 'Generic error when a stripelet has failed to load',
  defaultMessage: 'Unable to load this view',
});

function ErrorView() {
  const intl = useIntl();

  return <ErrorState title={intl.formatMessage(defaultErrorMessage)} />;
}

type InMemoryCache = ReturnType<typeof createCache>;

export const calculateMutedErrorsForComponent = (
  connectElement: ConnectElementImportKeys,
): string[] => {
  return [
    ...DEFAULT_GLOBALLY_MUTED_ERRORS, // GLobal muted errors
    ...(CONNECT_ELEMENT_IMPORTS[connectElement]?.errorConfiguration
      ?.mutedErrors ?? []), // Component specific errors
  ];
};

export const calculateExpectedErrorsForComponent = (
  connectElement: ConnectElementImportKeys,
  hostApp: string,
) => {
  const expectedErrors = [
    ...DEFAULT_GLOBALLY_EXPECTED_ERRORS, // Global expected errors
    ...(CONNECT_ELEMENT_IMPORTS[connectElement]?.errorConfiguration
      ?.expectedErrors ?? []), // Component specific errors
  ];

  if (READONLY_HOST_APPS.includes(hostApp)) {
    return [...expectedErrors, ...DEFAULT_READONLY_EXPECTED_ERRORS]; // Readonly expected errors
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  if (hostedOnboardingHostApps.includes(hostApp as HostApp)) {
    return [...expectedErrors, ...DEFAULT_HOSTED_ONBOARDING_EXPECTED_ERRORS]; // Hosted onboarding expected errors
  }

  return expectedErrors;
};

export default function ConnectSdkProvider({
  children,
  fetch,
  reportError,
  reportErrorOrSuccessLink,
  intlConfig,
  connectJsOptions,
  dataLayerFrame,
  connectElement,
  frameMessenger,
  // This will be used soon to build the v2 graphql provider
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  v2WorkspaceIdOverride,
}: {
  children: React.ReactNode;
  fetch: FetchOverrideFn;
  reportError: ReportError;
  reportErrorOrSuccessLink: ApolloLink;
  intlConfig: IntlConfig;
  connectJsOptions: ConnectJsOptions;
  dataLayerFrame: Window;
  connectElement: ConnectElementImportKeys;
  frameMessenger: IFrameMessenger;
  v2WorkspaceIdOverride: string | undefined;
}) {
  const {metaOptions} = connectJsOptions.getValues();
  const hostApp = metaOptions?.hostApp ?? 'platform';
  const {serviceEnvironment} = getCurrentScriptUrlContext();
  const [currentIntlConfig, setCurrentIntlConfig] = React.useState(intlConfig);
  const {setIsSessionInvalidOrExpired} = useEmbeddedInvalidOrExpiredSession();

  React.useEffect(() => {
    const observer = async () => {
      const intlConfig = await initSdkIntl(connectJsOptions.getValues().locale);

      setCurrentIntlConfig(intlConfig);
    };

    connectJsOptions.registerObserver(observer);

    return () => {
      connectJsOptions.unregisterObserver(observer);
    };
  }, [connectJsOptions]);

  const v2Config = React.useMemo(() => {
    return getV2SchemaConfig(
      async (url: string, options) => {
        const host = getStripeAccountsHost(serviceEnvironment);
        const absoluteURL = new URL(url, `https://${host}`);
        const requiredHeaders = Object.fromEntries([
          ['Authorization', `Bearer ${metaOptions.apiKeyOverride}`],
        ]);

        return fetch(absoluteURL.toString(), {
          ...options,
          headers: {
            ...options?.headers,
            ...requiredHeaders,
          },
        });
      },
      [],
      true,
    );
  }, [fetch, metaOptions.apiKeyOverride, serviceEnvironment]);

  const wrappedFetch: Fetch = React.useCallback(
    async (url: string, options) => {
      const apiHost = getStripeApiHost(serviceEnvironment);
      const absoluteURL = new URL(url, `https://${apiHost}`);

      return fetch(absoluteURL.toString(), options);
    },
    [fetch, serviceEnvironment],
  );

  const v1Config = React.useMemo(
    () =>
      getV1SchemaConfig([
        createDetectExpiredOrInvalidSessionLink((error) => {
          setIsSessionInvalidOrExpired(error);
        }),
        reportErrorOrSuccessLink,
      ]),
    [reportErrorOrSuccessLink, setIsSessionInvalidOrExpired],
  );

  // We share the graphql cache share between all of our components running in iframes
  let sharedCache: InMemoryCache;
  // Iframe case:
  if (dataLayerFrame?.__stripeGlobals?.apolloCache) {
    // Reuse the cache from the data layer if available
    sharedCache = dataLayerFrame.__stripeGlobals.apolloCache;
  } else {
    // Write our (new) cache back to the data layer
    // Future instances will use this cache instead of a new one.
    sharedCache = createCache(v1Config);
    dataLayerFrame.__stripeGlobals.apolloCache = sharedCache;
  }

  const dataConfig: DataConfig = React.useMemo((): DataConfig => {
    return {
      graphql: {
        disableDeadwood: true,
        sharedCache,
        schemas: {
          ...v1Config,
          ...v2Config,
        },
      },
      fetch: wrappedFetch,
      observability: {
        expectedApiErrors: calculateExpectedErrorsForComponent(
          connectElement,
          hostApp,
        ),
        mutedApiErrors: calculateMutedErrorsForComponent(connectElement),
      },
    };
  }, [wrappedFetch, v1Config, sharedCache, hostApp, connectElement, v2Config]);

  const uiConfig: UiConfig = React.useMemo(
    () => ({
      errorView: ErrorView,
      _internal_flags: {
        disable_stripelet_js_prefetching: false,
        disable_stripelet_queries_prefetching: false,
      },
    }),
    [],
  );

  const observabilityConfig = React.useMemo(
    () =>
      getObservabilityConfig({
        frameMessenger,
        connectElement,
        reportError,
        metaOptions,
      }),
    [frameMessenger, connectElement, reportError, metaOptions],
  );

  return (
    <ObservabilityProvider {...observabilityConfig}>
      <DataProvider config={dataConfig}>
        <IntlProvider config={currentIntlConfig}>
          <UiProvider config={uiConfig}>{children}</UiProvider>
        </IntlProvider>
      </DataProvider>
    </ObservabilityProvider>
  );
}
